import { Button, Dialog, DialogContent, DialogTitle, Divider, Stack, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import { stringComparerBy } from "@entrilia/shared/utilities/arrayHelper";
import adminApi from "../../../api/adminApi";
import type { ObjectAccessCategory, ObjectClassDefinition } from "../../../api/types/objectTypes";
import type { AccessValue } from "../../common/AccessEditor";
import AccessEditor from "../../common/AccessEditor";
import MultiselectAutocomplete from "../../common/MultiselectAutocomplete";
import type { User, UserAccess } from "./userAccessMatrixModel";
import {
  getCategoryOptions,
  mapObjectInternalUserPermissionsToUserAccess,
  mapUserAccessToObjectInternalUserPermissionsRequest,
} from "./userAccessMatrixModel";

interface Props {
  objectId: string;
  objectDefinition: ObjectClassDefinition;
  categories: ObjectAccessCategory[];
  currentAccessItems: UserAccess[];
  allUsers: User[];
  onSave: (updatedAccessItems: UserAccess[]) => void;
  onClose: () => void;
}

const updateObjectInternalUserPermissions = withErrorHandling(adminApi.updateObjectInternalUserPermissions);

const AddInternalUserAccessDialog = ({
  objectId,
  objectDefinition,
  categories,
  currentAccessItems,
  allUsers,
  onSave,
  onClose,
}: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const [isSaving, setIsSaving] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);

  const [accessValue, setAccessValue] = useState<AccessValue>({
    selectedRole: undefined,
    selectedCategoryIds: categories.map((c) => c.id),
  });

  const userOptions = useMemo(() => {
    const currentIds = currentAccessItems.map((c) => c.id);
    return allUsers.filter((c) => !currentIds.includes(c.id)).sort(stringComparerBy((o) => o.name));
  }, [allUsers, currentAccessItems]);

  const categoryOptions = useMemo(() => getCategoryOptions(categories), [categories]);

  const handleSave = async () => {
    const addedAccessItems: UserAccess[] = selectedUsers.map((c) => ({
      id: c.id,
      role: accessValue.selectedRole === "None" ? undefined : accessValue.selectedRole,
      categories: accessValue.selectedCategoryIds,
    }));

    const payload = {
      userPermissions: [...currentAccessItems, ...addedAccessItems].map(
        mapUserAccessToObjectInternalUserPermissionsRequest
      ),
    };

    setIsSaving(true);
    const [updatedUsers, error] = await updateObjectInternalUserPermissions(
      objectDefinition.objectType,
      objectId,
      payload
    );
    setIsSaving(false);

    if (error) {
      logError(error, "[AddInternalUserAccessDialog] updateObjectInternalUserPermissions");
      sendNotificationError(error.message);
      return;
    }

    sendNotification("User(s) successfully added to this company");
    onSave(updatedUsers.map(mapObjectInternalUserPermissionsToUserAccess));
    onClose();
  };

  const isValid = selectedUsers.length > 0;

  return (
    <>
      <Dialog open onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>Add New</DialogTitle>
        <DialogCloseButton onClick={onClose} />
        <Divider />

        <DialogContent>
          <Stack spacing={4}>
            <Stack spacing={2}>
              <Typography color="textSecondary">Select one or more existing internal users.</Typography>
              <MultiselectAutocomplete
                options={userOptions}
                values={selectedUsers}
                onSelected={(items) => setSelectedUsers(items)}
                getOptionValue={(option) => option.id}
                getOptionLabel={(option) => option.name}
                getOptionDescription={(option) => option.email}
                placeholder="Internal Users"
                noItemsText="No users found"
              />
            </Stack>

            <AccessEditor
              value={accessValue}
              onChange={(update) => setAccessValue((prev) => ({ ...prev, ...update }))}
              roleOptions={[]}
              categoryOptions={categoryOptions}
            />
          </Stack>
        </DialogContent>

        <Divider />
        <DialogButtons onCancel={onClose}>
          <Button variant="contained" loading={isSaving} onClick={handleSave} disabled={!isValid}>
            Add
          </Button>
        </DialogButtons>
      </Dialog>
    </>
  );
};

export default AddInternalUserAccessDialog;
